import React from "react";
import TiltCard from "components/tiltCard/TiltCard";
import type { Theme } from "types";

const TAGS = ["Microservices", "Cloud Native", "Product Thinking"];

interface ProfileCardProps {
  theme: Theme;
  nameText: string;
  nickname?: string;
  fullName?: string;
  profileImageSrc: string | null;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  nameText: _nameText,
  nickname,
  fullName,
  profileImageSrc,
}) => (
  <div className="relative w-full max-w-[400px] mx-auto lg:max-w-none lg:order-2">
    <TiltCard>
      <div className="relative border border-border bg-transparent p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between pb-3.5 mb-4 border-b border-border opacity-60">
          <span className="inline-flex items-center gap-2 text-[10px] font-medium tracking-[0.16em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            Profile
          </span>
          <span className="text-[10px] font-medium tracking-[0.14em] uppercase border-b border-border">
            Backend • Cloud • AI
          </span>
        </div>

        {/* Photo */}
        <div className="relative border border-border overflow-hidden grayscale hover:grayscale-0 transition-all duration-400">
          {profileImageSrc ? (
            <img
              src={profileImageSrc}
              alt={`${fullName} profile`}
              className="block w-full h-[400px] sm:h-[460px] object-cover object-top"
            />
          ) : null}
        </div>

        {/* Nameplate overlay */}
        <div className="absolute inset-x-0 bottom-0 p-4">
          <div className="flex items-end justify-between gap-3.5 border border-white/30 bg-black/55 backdrop-blur-xl saturate-0 p-4">
            <div className="flex-1 min-w-0">
              {nickname && (
                <p className="text-[9px] font-semibold tracking-[0.22em] uppercase text-white/50 mb-1">
                  {nickname}
                </p>
              )}
              {fullName && (
                <h3 className="text-lg sm:text-xl font-semibold leading-tight text-white">
                  {fullName}
                </h3>
              )}
              <p className="text-[11px] leading-relaxed text-white/55 mt-1.5">
                Software engineer focused on scalable platforms, thoughtful
                architecture, and product-minded execution.
              </p>
            </div>
            <div className="hidden sm:flex w-11 h-11 items-center justify-center border border-white/25 text-white/70 flex-shrink-0">
              <svg
                className="w-5.5 h-5.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="grid grid-cols-3 border-t border-border mt-4">
          {TAGS.map((tag) => (
            <div
              key={tag}
              className="py-3 text-[9px] font-semibold tracking-[0.14em] uppercase text-center border-r border-border last:border-r-0 opacity-55"
            >
              {tag}
            </div>
          ))}
        </div>
      </div>
    </TiltCard>
  </div>
);
